import type { MarketCardPriceType } from 'constants/markets';
import type { PortfolioMarket, PortfolioPosition } from 'models/portfolioEvent';
import { asCents, toMoney, toMoneyWithSign } from 'utils/caseing';
import { isoToDateTimeStringWithTz } from 'utils/time';

/**
 * One row in the "Download portfolio data" CSV. Pure data, plain object,
 * built from a `PortfolioMarket` + `PortfolioPosition` snapshot plus a couple
 * of context values the events store doesn't carry (the user's market-price
 * preference and the series title). Keys match the column headers `react-csv`
 * writes out.
 */
export interface AdvancedPortfolioCSV {
    marketTicker: string;
    eventTitle: string;
    marketTitle: string;
    closeDate: string;
    volume: number;
    yesPrice: string;
    noPrice: string;
    lastTradePrice: string;
    position: string;
    averagePrice: string;
    exposure: string;
    positionReturns: string;
    lockedInReturns: string;
    totalReturns: string;
}

export interface AdvancedPortfolioCsvOptions {
    /** Title pulled from the corresponding `PortfolioEvent`. */
    eventTitle: string;
    /** The user's `marketCardPriceType` preference — controls the yes/no price column. */
    marketCardPriceType: MarketCardPriceType;
}

const yesPriceInCentsAsString = (market: PortfolioMarket): string =>
    market.yesAsk === 100 ? '--¢' : asCents(market.yesAsk);

const noPriceInCentsAsString = (market: PortfolioMarket): string =>
    market.noAsk === 100 ? '--¢' : asCents(market.noAsk);

const lastTradedYesPriceInCentsAsString = (market: PortfolioMarket): string =>
    market.lastPrice === 0 ? '--¢' : asCents(market.lastPrice);

const userPreferenceYesPriceString = (market: PortfolioMarket, marketCardPriceType: MarketCardPriceType): string => {
    const lastTraded = marketCardPriceType === 'traded' || market.isClosed;
    return lastTraded ? lastTradedYesPriceInCentsAsString(market) : yesPriceInCentsAsString(market);
};

const userPreferenceNoPriceString = (market: PortfolioMarket, marketCardPriceType: MarketCardPriceType): string => {
    const lastTraded = marketCardPriceType === 'traded' || market.isClosed;
    const lastTradedNo = market.lastPrice === 0 ? '--¢' : asCents(100 - market.lastPrice);
    return lastTraded ? lastTradedNo : noPriceInCentsAsString(market);
};

const positionLabel = (position: PortfolioPosition): string => {
    if (position.position === 0) return 'None';
    const side = position.position > 0 ? 'Yes' : 'No';
    return `${side} x ${Math.abs(position.position)}`;
};

/**
 * Build a single CSV row from the lean `PortfolioMarket` + `PortfolioPosition`
 * pair the events store carries. Column order matches what `react-csv`
 * expects.
 */
export const toAdvancedPortfolioCSV = (
    market: PortfolioMarket,
    position: PortfolioPosition,
    { eventTitle, marketCardPriceType }: AdvancedPortfolioCsvOptions,
): AdvancedPortfolioCSV => ({
    marketTicker: market.ticker,
    eventTitle,
    marketTitle: market.subtitle,
    closeDate: isoToDateTimeStringWithTz(market.closeDate),
    volume: market.volume,
    yesPrice: userPreferenceYesPriceString(market, marketCardPriceType),
    noPrice: userPreferenceNoPriceString(market, marketCardPriceType),
    lastTradePrice: lastTradedYesPriceInCentsAsString(market),
    position: positionLabel(position),
    averagePrice: `${(position.averagePriceInDollarsWithFees * 100).toFixed(0)}¢`,
    exposure: toMoney(position.exposureInDollars),
    positionReturns: toMoneyWithSign(position.unrealizedReturnsInDollars),
    lockedInReturns: toMoneyWithSign(position.realizedReturnInDollars),
    totalReturns: toMoneyWithSign(position.totalReturnsInDollars),
});
